import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import VideoEmbed from "../components/VideoEmbed";

export default function FreePreview() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api
      .get(`/courses/${slug}/free-preview`)
      .then((r) => setData(r.data))
      .catch((e) => {
        console.error("Failed to load preview", e);
        setError(e.response?.status === 404 ? "Preview not available" : "Failed to load preview");
      })
      .finally(() => setLoading(false));
  }, [slug]);

  return (
    <div className="min-h-screen bg-black text-white flex flex-col" data-testid="free-preview-page">
      {/* Minimal top bar */}
      <header className="bg-[#060A24] border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to={`/courses/${slug}`} className="flex items-center gap-2 text-white/70 hover:text-white text-xs sm:text-sm font-bold tracking-wider" data-testid="back-to-course">
            <ArrowLeft size={14} /> BACK TO COURSE
          </Link>
          <Link to="/" className="flex items-center gap-2 text-white">
            <img src="/brand/freddy-symbol.webp" alt="Freddy" className="h-7 w-auto" />
            <span className="hidden sm:inline text-[10px] sm:text-xs tracking-[0.18em] font-black">FREDDY COMPUTE ACADEMY</span>
          </Link>
          <Link to="/login" className="text-xs sm:text-sm text-white/70 hover:text-white font-bold tracking-wider" data-testid="preview-login-link">
            LOGIN
          </Link>
        </div>
      </header>

      <main className="flex-1 flex flex-col">
        <div className="max-w-5xl mx-auto w-full px-4 sm:px-8 py-10 flex-1 flex flex-col">
          {loading ? (
            <p className="text-white/60 m-auto">Loading preview…</p>
          ) : error || !data ? (
            <div className="m-auto text-center">
              <p className="text-white/70 mb-6">{error || "Preview unavailable"}</p>
              <Link to="/courses" className="btn-fc-red">Browse all courses</Link>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <p className="fc-overline mb-2">FREE PREVIEW · LESSON 1</p>
                <h1 className="fc-h-section text-3xl sm:text-4xl mb-2" data-testid="preview-course-title">
                  {data.course_title}
                </h1>
                <p className="text-white/70 text-lg" data-testid="preview-lesson-title">{data.lesson_title}</p>
              </div>

              <div className="bg-black border border-white/10 aspect-video w-full mb-8 overflow-hidden shadow-[0_24px_60px_-20px_rgba(0,0,0,0.8)]">
                <VideoEmbed
                  url={data.video_url}
                  className="w-full h-full"
                  testid="preview-video-player"
                />
              </div>

              <div className="bg-[#060A24] border border-white/10 p-6 sm:p-8 mb-8">
                <p className="fc-overline mb-3">TRANSCRIPT SNIPPET</p>
                <p className="text-white/80 leading-relaxed text-sm sm:text-base" data-testid="preview-transcript">
                  {data.transcript_snippet}
                </p>
              </div>

              <div className="bg-fc-navy border border-fc-red/40 p-6 sm:p-8 text-center">
                <p className="fc-overline mb-3">WANT MORE?</p>
                <h2 className="fc-h-section text-2xl sm:text-3xl mb-4">
                  Register free to continue the full <span className="text-fc-red">{data.course_title}</span> series.
                </h2>
                <Link to={`/register?course=${data.course_slug}`} className="btn-fc-red inline-flex" data-testid="preview-register-cta">
                  Continue Learning <ArrowRight size={16} />
                </Link>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
